import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    TextField, 
    Typography, 
    MenuItem, 
    Select, 
    FormControl, 
    InputLabel, 
    Grid,
    CircularProgress,
    Tabs,
    Tab,
    Paper,
    IconButton,
} from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';
import { CloudFunctions } from '../../../../utils/enums/CloudFunctions';
import BaseModal from '../../../../components/modals/BaseModal';
import FileUpload from '../../../../components/inputs/FileUpload';
import PreviewIcon from '@mui/icons-material/Preview';
import { getStripeInvoiceSettings, updateStripeInvoiceSettings } from '../../../../repository/StripeSettingsDAO';
import { useUserContext } from '../../../../context/user_context/UserContext';

function CustomizeInvoicesModal({ show, onHide, accountId }) {
    const { t } = useTranslation();
    const { firebaseUser } = useUserContext();;
    const functions = getFunctions();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [settings, setSettings] = useState(null);

    // Load saved settings when modal opens
    useEffect(() => {
        const loadSettings = async () => {
            if (show) {
                setLoading(true);
                const result = await getStripeInvoiceSettings(accountId);
                if (result.success) {
                    setSettings(result.data);
                }
                setLoading(false);
            }
        };
        loadSettings();
    }, [show, accountId]);

    const handleSave = async () => {
        setLoading(true);
        try {
            const organizationId = firebaseUser.organization;
            const updateInvoiceSettings = httpsCallable(functions, CloudFunctions.UpdateInvoiceSettings);
            await updateInvoiceSettings({ accountId, settings, organizationId });
            onHide();
        } catch (error) {
            console.error("Error updating invoice settings:", error);
            // You might want to show an error message to the user here
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async () => {
        setLoading(true);
        const generatePreview = httpsCallable(functions, CloudFunctions.GenerateInvoicePreview);
        try {
            const result = await generatePreview({ accountId, settings });
            window.open(result.data.hostedInvoiceUrl, '_blank');
        } catch (error) {
            console.error("Error generating preview:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async (file) => {
        // Implement logo upload to your storage solution
        // Update settings.logoUrl with the uploaded file URL
    };

    const actions = (
        <>
            <Button 
                variant="outlined" 
                onClick={handlePreview}
                startIcon={<PreviewIcon />}
                disabled={loading}
            >
                {t(StringKeys.PREVIEW)}
            </Button>
            <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSave}
                disabled={loading}
            >
                {loading ? <CircularProgress size={24} /> : t(StringKeys.SAVE)}
            </Button>
        </>
    );

    const renderBrandingTab = () => (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                    {t(StringKeys.PRIMARY_COLOR)}
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <HexColorPicker 
                        color={settings.primaryColor}
                        onChange={(color) => setSettings({ ...settings, primaryColor: color })}
                    />
                    <TextField
                        value={settings.primaryColor}
                        onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                    />
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                    {t(StringKeys.SECONDARY_COLOR)}
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <HexColorPicker 
                        color={settings.secondaryColor}
                        onChange={(color) => setSettings({ ...settings, secondaryColor: color })}
                    />
                    <TextField
                        value={settings.secondaryColor}
                        onChange={(e) => setSettings({ ...settings, secondaryColor: e.target.value })}
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                    />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <FileUpload 
                    onFileSelect={handleLogoUpload}
                    label={t(StringKeys.UPLOAD_LOGO)}
                    accept="image/*"
                />
            </Grid>
        </Grid>
    );

    const renderContentTab = () => (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
                <TextField
                    label={t(StringKeys.DEFAULT_MEMO)}
                    value={settings.defaultMemo}
                    onChange={(e) => setSettings({ ...settings, defaultMemo: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label={t(StringKeys.DEFAULT_FOOTER)}
                    value={settings.defaultFooter}
                    onChange={(e) => setSettings({ ...settings, defaultFooter: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label={t(StringKeys.STATEMENT_DESCRIPTOR)}
                    value={settings.statementDescriptor}
                    onChange={(e) => setSettings({ ...settings, statementDescriptor: e.target.value })}
                    fullWidth
                    helperText={t(StringKeys.STATEMENT_DESCRIPTOR_HELP)}
                />
            </Grid>
        </Grid>
    );

    const renderPaymentSettingsTab = () => (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <InputLabel>{t(StringKeys.PAYMENT_TERMS)}</InputLabel>
                    <Select
                        value={settings.paymentTerms}
                        onChange={(e) => setSettings({ ...settings, paymentTerms: e.target.value })}
                        label={t(StringKeys.PAYMENT_TERMS)}
                    >
                        <MenuItem value={7}>7 {t(StringKeys.DAYS)}</MenuItem>
                        <MenuItem value={15}>15 {t(StringKeys.DAYS)}</MenuItem>
                        <MenuItem value={30}>30 {t(StringKeys.DAYS)}</MenuItem>
                        <MenuItem value={60}>60 {t(StringKeys.DAYS)}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <InputLabel>{t(StringKeys.DEFAULT_CURRENCY)}</InputLabel>
                    <Select
                        value={settings.defaultCurrency}
                        onChange={(e) => setSettings({ ...settings, defaultCurrency: e.target.value })}
                        label={t(StringKeys.DEFAULT_CURRENCY)}
                    >
                        <MenuItem value="usd">USD</MenuItem>
                        <MenuItem value="eur">EUR</MenuItem>
                        <MenuItem value="gbp">GBP</MenuItem>
                        {/* Add more currencies as needed */}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );

    // Only render the modal content when settings are loaded
    if (!settings) {
        return null;
    }

    return (
        <BaseModal
            open={show}
            onClose={onHide}
            title={t(StringKeys.CUSTOMIZE_INVOICES)}
            actions={actions}
            maxWidth="md"
            fullWidth
        >
            <Box sx={{ p: 2 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label={t(StringKeys.BRANDING)} />
                    <Tab label={t(StringKeys.CONTENT)} />
                    <Tab label={t(StringKeys.PAYMENT_SETTINGS)} />
                </Tabs>

                <Box sx={{ height: 400, overflowY: 'auto' }}>
                    {activeTab === 0 && renderBrandingTab()}
                    {activeTab === 1 && renderContentTab()}
                    {activeTab === 2 && renderPaymentSettingsTab()}
                </Box>
                
                {previewUrl && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t(StringKeys.INVOICE_PREVIEW_OPENED_NEW_TAB)}
                        </Typography>
                    </Box>
                )}
            </Box>
        </BaseModal>
    );
}

export default CustomizeInvoicesModal;
